/* eslint-disable max-lines-per-function */
import './CoralGlobalFooter.scss'

import { useIsWindowBiggerThan } from '@bob/library'
import { getCookieConsent } from '@websrc/App/Helpers/cookieUtil'
import { useStaticText } from '@websrc/App/Hooks/useStaticText'
import { mapCookiePolicy } from '@websrc/DataMappers/mapCookiePolicy'
import { mapCoralWebGlobalFooter } from '@websrc/DataMappers/mapCoralGlobalFooter'
import React, { FC, useLayoutEffect, useMemo, useRef, useState } from 'react'

import IconProvider from '../../../../../coral-library/icons/IconProvider'
import { Baseplate, StudRange } from '../../Baseplate/Baseplate'
import { CookieSettings } from '../../CookieConsent/CookieConsent'
import { CoralAnimatedCTA } from '../../CoralAnimatedCTA/CoralAnimatedCTA'
import { CoralLogo } from '../../CoralLogo/CoralLogo'
import { CTA } from '../../CTA'

type CoralGlobalFooterProps = ReturnType<typeof mapCoralWebGlobalFooter> & {
  cookiePolicy: ReturnType<typeof mapCookiePolicy>
  hideCta?: boolean
  isMarketingPage?: boolean
}

export const CoralGlobalFooter: FC<CoralGlobalFooterProps> = (props) => {
  const {
    isMarketingPage,
    simple_footer,
    cta_section,
    cookiePolicy,
    copyright,
    terms_policy,
    link_section,
    share_section,
    hideCta,
  } = props

  const isMobile = !useIsWindowBiggerThan(719)
  const isTablet = useIsWindowBiggerThan(719)
  const isDesktop = useIsWindowBiggerThan(1079)
  const isWidescreen = useIsWindowBiggerThan(1439)

  const wrapperRef = useRef<HTMLDivElement>(null)
  const [showCookieSettings, setShowCookieSettings] = useState(false)
  const [dynamicRows, setDynamicRows] = useState(1)
  const currentConsent = getCookieConsent()
  const t = useStaticText()
  const fixedUnits = useMemo(() => {
    return isWidescreen ? 64 : isDesktop ? 45 : isTablet ? 36 : 48
  }, [isTablet, isDesktop, isWidescreen])
  const cookies = cookiePolicy.preferences.map((pref) => ({
    ...pref,
    selected:
      pref.category === 'necessary' ||
      Boolean(currentConsent?.preferences?.find((cat) => cat.key === pref.category && cat.value)),
  }))
  let isCtaHidden = isMarketingPage || hideCta
  const isSimpleFooter = isMarketingPage || simple_footer
  if (isSimpleFooter) isCtaHidden = true

  useLayoutEffect(() => {
    function recalculateRows() {
      if (wrapperRef.current) {
        const height = wrapperRef.current.offsetHeight
        const rows = Math.max(1, Math.round(height / fixedUnits))
        setDynamicRows(rows)
      }
    }
    recalculateRows()
    window.addEventListener('resize', recalculateRows)
    return () => window.removeEventListener('resize', recalculateRows)
  }, [isWidescreen, isDesktop, isTablet, isMobile, fixedUnits])

  const studRangeImage = useMemo((): StudRange[] => {
    if (isTablet) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: dynamicRows, columns: 3 },
          offset: { top: 0, right: 0, bottom: 0, left: 0 },
        },
      ]
    } else if (isMobile) {
      return [
        {
          snapPosition: 'top-left',
          size: { rows: dynamicRows, columns: 2 },
          offset: { top: 0, right: 0, bottom: 0, left: 0 },
        },
      ]
    } else {
      return []
    }
  }, [isMobile, isTablet, dynamicRows])

  return (
    <>
      {!isCtaHidden && <CoralAnimatedCTA {...cta_section} />}
      <footer
        className={'coral-global-footer-container'}
        id={'main-global-footer'}
        ref={wrapperRef}
      >
        <Baseplate
          className={'baseplate--fixed-units'}
          fixedUnit={fixedUnits}
          studRanges={studRangeImage}
        >
          <div className={'coral-global-footer-baseplate'}>
            <div className={'coral-global-footer'}>
              <CoralLogo
                className={'coral-global-footer__logo'}
                isDarkBackground={false}
                width={isDesktop ? 240 : 148}
              />
              {!isSimpleFooter ? (
                <>
                  <div className={'coral-global-footer__link-groups-section'}>
                    {link_section.link_group.map((group, idx) => (
                      <div className={'coral-global-footer__link-group'} key={idx}>
                        <div className={'coral-global-footer__link-group-title'}>{group.title}</div>
                        <div className={'coral-global-footer__link-group-links'}>
                          {group.ctas?.map((cta, ctaIdx) => (
                            // ...existing code...
                            <CTA
                              className={`coral-global-button m--type-standalone-link m--fit-content ${
                                isDesktop ? 'm--size-lg' : isTablet ? 'm--size-md' : 'm--size-sm'
                              }`}
                              cta={cta}
                              iconName={cta?.__ctaType === 'external' ? 'upRightArrow' : ''}
                              key={ctaIdx}
                              type={'clean'}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className={'coral-global-footer__share-section'}>
                    <div className={'coral-global-footer__share-header'}>
                      {share_section?.header}
                    </div>
                    <div className={'coral-global-footer__share-icons-container'}>
                      {share_section?.share_media &&
                        'media' in share_section.share_media &&
                        Array.isArray(share_section.share_media.media) &&
                        share_section.share_media.media.map(
                          (media: { icon?: string | null; url?: string | null }, idx: number) => (
                            <div key={idx}>
                              {typeof media.icon === 'string' && media.icon && (
                                <a
                                  aria-label={`${media.icon}`}
                                  href={media.url ?? undefined}
                                  rel={'noopener noreferrer'}
                                  target={'_blank'}
                                >
                                  <IconProvider name={media.icon} size={32} />
                                </a>
                              )}
                            </div>
                          )
                        )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={'coral-global-footer__share-section'}>
                    <div className={'coral-global-footer__share-header'}>
                      {share_section?.header}
                    </div>
                  </div>
                </>
              )}
              <hr className={'coral-global-footer__divider'} />
              <div className={'coral-global-footer__policy-section'}>
                <div className={'coral-global-footer__policy-links'}>
                  {terms_policy.map((link: { url: string; title: string }, key: number) => (
                    <a className={'coral-global-footer__policy-link'} href={link.url} key={key}>
                      <span>{link.title}</span>
                    </a>
                  ))}
                  <button
                    className={'coral-global-footer__button'}
                    onClick={() => setShowCookieSettings(true)}
                  >
                    {t.cookies.cookie_settings}
                  </button>
                </div>
                <div>{copyright}</div>
                {showCookieSettings && (
                  <CookieSettings
                    acceptSettingsCallback={() => setShowCookieSettings(false)}
                    cancelSettingsCallback={() => setShowCookieSettings(false)}
                    current={cookies}
                  />
                )}
              </div>
            </div>
          </div>
        </Baseplate>
      </footer>
    </>
  )
}
